import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  AlertTypesPayload,
  NotificationSettings,
  NotificationSettingsPayload,
  NotificationSubscriptions,
  SubscriptionsPayload,
} from "entities/notifications";

export type SaveSettingsPayload = {
  userId: number;
  body: NotificationSettingsPayload;
};

export type SaveAllPayload = {
  userId: number;
  channels: NotificationSettingsPayload;
  alertTypes: AlertTypesPayload;
  subscriptions: SubscriptionsPayload;
};

export type SendWhatsappCodeActionPayload = {
  userId: number;
  phone: string;
};

export type VerifyWhatsappCodeActionPayload = {
  userId: number;
  phone: string;
  code: string;
};

type NotificationSettingsState = {
  settings: NotificationSettings | null;
  subscriptions: NotificationSubscriptions | null;
  isLoadingSubscriptions: boolean;
  isLoading: boolean;
  isSaving: boolean;
  errors: { detail: Record<string, string> } | null;
  codeSending: boolean;
  codeSent: boolean;
  codeVerifying: boolean;
  codeError: string | null;
  whatsappJustVerified: boolean;
  rateLimited: boolean;
  rateLimitRetryAfter: number | null;
};

const initialState: NotificationSettingsState = {
  settings: null,
  subscriptions: null,
  isLoadingSubscriptions: false,
  isLoading: false,
  isSaving: false,
  errors: null,
  codeSending: false,
  codeSent: false,
  codeVerifying: false,
  codeError: null,
  whatsappJustVerified: false,
  rateLimited: false,
  rateLimitRetryAfter: null,
};

const notificationSettingsSlice = createSlice({
  name: "notificationSettings",
  initialState,
  reducers: {
    getSettingsRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isLoading = true;
      state.errors = null;
    },
    getSettingsSuccess: (state, action: PayloadAction<NotificationSettings>) => {
      state.settings = action.payload;
      state.isLoading = false;
    },
    getSettingsFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },
    saveSettingsRequest: (state, action: PayloadAction<SaveSettingsPayload>) => {
      void action.payload;
      state.isSaving = true;
      state.errors = null;
    },
    saveSettingsSuccess: (state, action: PayloadAction<NotificationSettings>) => {
      state.settings = action.payload;
      state.isSaving = false;
    },
    saveSettingsFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isSaving = false;
    },
    getSubscriptionsRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isLoadingSubscriptions = true;
    },
    getSubscriptionsSuccess: (state, action: PayloadAction<NotificationSubscriptions>) => {
      state.subscriptions = action.payload;
      state.isLoadingSubscriptions = false;
    },
    getSubscriptionsFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoadingSubscriptions = false;
    },
    saveAllRequest: (state, action: PayloadAction<SaveAllPayload>) => {
      void action.payload;
      state.isSaving = true;
      state.errors = null;
    },
    saveAllSuccess: (
      state,
      action: PayloadAction<{ settings: NotificationSettings; subscriptions: NotificationSubscriptions }>,
    ) => {
      state.settings = action.payload.settings;
      state.subscriptions = action.payload.subscriptions;
      state.isSaving = false;
    },
    saveAllFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isSaving = false;
    },
    sendWhatsappCodeRequest: (state, action: PayloadAction<SendWhatsappCodeActionPayload>) => {
      void action.payload;
      state.codeSending = true;
      state.codeError = null;
      state.rateLimited = false;
      state.rateLimitRetryAfter = null;
    },
    sendWhatsappCodeSuccess: (state) => {
      state.codeSending = false;
      state.codeSent = true;
      state.rateLimited = false;
      state.rateLimitRetryAfter = null;
    },
    sendWhatsappCodeFailure: (state, action: PayloadAction<string>) => {
      state.codeSending = false;
      state.codeError = action.payload;
    },
    whatsappSendRateLimited: (state, action: PayloadAction<number>) => {
      state.codeSending = false;
      state.rateLimited = true;
      state.rateLimitRetryAfter = action.payload;
    },
    consumeWhatsappRateLimit: (state) => {
      state.rateLimited = false;
      state.rateLimitRetryAfter = null;
    },
    verifyWhatsappCodeRequest: (state, action: PayloadAction<VerifyWhatsappCodeActionPayload>) => {
      void action.payload;
      state.codeVerifying = true;
      state.codeError = null;
    },
    verifyWhatsappCodeSuccess: (state, action: PayloadAction<NotificationSettings>) => {
      state.codeVerifying = false;
      state.settings = action.payload;
      state.whatsappJustVerified = true;
    },
    verifyWhatsappCodeFailure: (state, action: PayloadAction<string>) => {
      state.codeVerifying = false;
      state.codeError = action.payload;
    },
    resetWhatsappVerification: (state) => {
      state.codeSending = false;
      state.codeSent = false;
      state.codeVerifying = false;
      state.codeError = null;
      state.rateLimited = false;
      state.rateLimitRetryAfter = null;
    },
    consumeWhatsappJustVerified: (state) => {
      state.whatsappJustVerified = false;
    },
    clearSettings: (state) => {
      state.settings = null;
      state.subscriptions = null;
      state.errors = null;
      state.codeSending = false;
      state.codeSent = false;
      state.codeVerifying = false;
      state.codeError = null;
      state.whatsappJustVerified = false;
      state.rateLimited = false;
      state.rateLimitRetryAfter = null;
    },
  },
});

export const {
  getSettingsRequest,
  getSettingsSuccess,
  getSettingsFailure,
  saveSettingsRequest,
  saveSettingsSuccess,
  saveSettingsFailure,
  getSubscriptionsRequest,
  getSubscriptionsSuccess,
  getSubscriptionsFailure,
  saveAllRequest,
  saveAllSuccess,
  saveAllFailure,
  sendWhatsappCodeRequest,
  sendWhatsappCodeSuccess,
  sendWhatsappCodeFailure,
  verifyWhatsappCodeRequest,
  verifyWhatsappCodeSuccess,
  verifyWhatsappCodeFailure,
  whatsappSendRateLimited,
  consumeWhatsappRateLimit,
  resetWhatsappVerification,
  consumeWhatsappJustVerified,
  clearSettings,
} = notificationSettingsSlice.actions;
export default notificationSettingsSlice.reducer;
